import moment from 'moment';
import { sumBy, toNumber, isUndefined } from 'lodash';
import { computeCareerServiceBonusTodaysDollars, computeCareerServiceBonusFutureDollars } from '@/js/routes/redux/store/utilities/reduxAllNumbersHelpers';
import { computeContinuationPayTodaysDollars, computeContinuationPayFutureDollars } from '@/js/routes/brs/store/utilities/brsAllNumbersHelpers';
import { getRcActiveDutyServiceTime } from '@/js/store/calculator/utilities/reserveComponentHelpers';

//  ****************************************************************************************************
//    Aggregate common data for each month of a service member's career.
//  ****************************************************************************************************
function getCommonAllNumbersByMonthFromRunDateToDeathDate(serviceMember) {
  const rundate = serviceMember.rundate();
  const retSepDate = serviceMember.retSepDate();
  const allNumbersByMonth = [];

  serviceMember.results.allNumbersByMonth.forEach((month) => {
    const calendarYear = Number(month.calendarYear);
    const calendarMonth = Number(month.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    if (calendarDate.isBefore(rundate)) return;

    const allNumbersData = {};
    allNumbersData.recordId = month.recordId;

    allNumbersData.calendarYear = month.calendarYear;
    allNumbersData.calendarMonth = month.calendarMonth;
    allNumbersData.calendarMonthAbbreviation = month.calendarMonthAbbreviation;

    allNumbersData.ageYears = month.ageYears;
    allNumbersData.ageMonths = month.ageMonths;
    allNumbersData.ageCompletedYears = month.ageCompletedYears;

    //  Only include fields while in service.
    if (calendarDate.isSameOrBefore(retSepDate)) {
      allNumbersData.pebdServiceYear = month.pebdServiceYear;
      allNumbersData.pebdServiceMonth = month.pebdServiceMonth;
      allNumbersData.pebdPresentServiceYear = month.pebdPresentServiceYear;
      allNumbersData.pebdPresentServiceMonth = month.pebdPresentServiceMonth;
      allNumbersData.pebdCompletedServiceYear = month.pebdCompletedServiceYear;
      allNumbersData.pebdCompletedServiceMonth = month.pebdCompletedServiceMonth;
      allNumbersData.pebdCompletedServiceYears = month.pebdCompletedServiceYears;

      allNumbersData.asdServiceYear = month.asdServiceYear;
      allNumbersData.asdServiceMonth = month.asdServiceMonth;
      allNumbersData.asdPresentServiceYear = month.asdPresentServiceYear;
      allNumbersData.asdPresentServiceMonth = month.asdPresentServiceMonth;
      allNumbersData.asdCompletedServiceYear = month.asdCompletedServiceYear;
      allNumbersData.asdCompletedServiceMonth = month.asdCompletedServiceMonth;
      allNumbersData.asdCompletedServiceYears = month.asdCompletedServiceYears;

      allNumbersData.suggestedRank = month.suggestedRank;
      allNumbersData.overrideRank = month.overrideRank;

      allNumbersData.component = month.component;
    }

    allNumbersByMonth.push(allNumbersData);
  });

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Add a unique numeric ID to each record in the array.
//  ****************************************************************************************************
function addRecordId(careerMonths, serviceMember) {
  const numberOfMonthsInLife = serviceMember.numberOfMonthsInLife();

  for (let recordId = 1; recordId <= numberOfMonthsInLife; recordId += 1) {
    careerMonths.push({
      recordId,
    });
  }

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the calendar year/month to each record in the array starting at the service member's
//    date of birth and ending at the death date.
//  ****************************************************************************************************
function addCalendarYearMonth(careerMonths, dob) {
  let year = dob.year();
  let month = dob.month() + 1;

  careerMonths.forEach((careerMonth) => {
    careerMonth.calendarYear = year;
    careerMonth.calendarMonth = month;
    careerMonth.calendarMonthAbbreviation = moment([year, month - 1, 1]).format('MMM');

    if (month === 12) {
      year += 1;
      month = 1;
    } else {
      month += 1;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the service member's monthly age to each record in the array.
//  ****************************************************************************************************
function addAge(careerMonths) {
  let years = 0;
  let months = 0;

  careerMonths.forEach((careerMonth) => {
    careerMonth.ageYears = years;
    careerMonth.ageMonths = months;

    if (months === 11) {
      years += 1;
      months = 0;
    } else {
      months += 1;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the service member's present age starting at 1y1m old.
//  ****************************************************************************************************
function addAgePresentYears(careerMonths) {
  let presentAgeYear = 1;
  let presentAgeMonth = 1;

  careerMonths.forEach((careerMonth) => {
    careerMonth.agePresentYear = presentAgeYear;
    careerMonth.agePresentMonth = presentAgeMonth;

    if (presentAgeMonth === 12) {
      presentAgeYear += 1;
      presentAgeMonth = 1;
    } else {
      presentAgeMonth += 1;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the service member's yearly age at each date of birth month.
//  ****************************************************************************************************
function addAgeCompletedYears(careerMonths) {
  careerMonths.forEach((careerMonth) => {
    careerMonth.ageCompletedYears = -1;
    if (careerMonth.ageYears > 0 && careerMonth.ageMonths === 0) {
      careerMonth.ageCompletedYears = careerMonth.ageYears;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Pay Entry Base Date (PEBD) ~ when the service member joined the service.
//    Add the service member's service year/month to each record in the array.
//
//    The common nomenclature for when service member's speak about their career is to state
//    "I am currently in my 1st year of service, 2nd month". The equates to service year "1Y2M"
//    This actually translates to being the service member being in their 2nd month of duty having
//    completed 0 years and only 1 month.
//  ****************************************************************************************************
function addPebdServiceYearsAndMonths(careerMonths, pebd, retSepDate) {
  let serviceYear = 1;
  let serviceMonth = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.pebdServiceYear = -1;
    careerMonth.pebdServiceMonth = -1;
    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.pebdServiceYear = serviceYear;
      careerMonth.pebdServiceMonth = serviceMonth;

      if (serviceMonth === 12) {
        serviceYear += 1;
        serviceMonth = 1;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Pay Entry Base Date (PEBD) ~ when the service member joined the service.
//    Add the service member's present service year/month to each record in the array.
//
//    This translates to the service member's completed years of service and the current month of duty.
//    2Y4M translates to the service member having completed 2 years of service and currently in their
//    4th month of duty. But they have not completed the 4th month of duty yet.
//  ****************************************************************************************************
function addPebdPresentServiceYearsAndMonths(careerMonths, pebd, retSepDate) {
  let serviceYear = 0;
  let serviceMonth = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.pebdPresentServiceYear = -1;
    careerMonth.pebdPresentServiceMonth = -1;
    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.pebdPresentServiceYear = serviceYear;
      careerMonth.pebdPresentServiceMonth = serviceMonth;

      if (serviceMonth === 12) {
        serviceYear += 1;
        serviceMonth = 1;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Pay Entry Base Date (PEBD) ~ when the service member joined the service.
//    Add the service member's current service year/month to each record in the array.
//
//    This translates to the service member's completed years and months of service.
//    2Y4M translates to the service member having completed 2 years and 4 months of duty.
//  ****************************************************************************************************
function addPebdCompletedServiceYearsAndMonths(careerMonths, pebd, retSepDate) {
  let serviceYear = 0;
  let serviceMonth = 0;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.pebdCompletedServiceYear = -1;
    careerMonth.pebdCompletedServiceMonth = -1;
    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.pebdCompletedServiceYear = serviceYear;
      careerMonth.pebdCompletedServiceMonth = serviceMonth;

      if (serviceMonth === 11) {
        serviceYear += 1;
        serviceMonth = 0;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Pay Entry Base Date (PEBD) ~ when the service member joined the service.
//    Add the service member's completed service years. 12 months of completed duty = 1 service year.
//
//    This translates to the service member's completed years of service.
//    3Y translates to the service member completing a full 3 years (or 36 months) of service.
//    Completed service years are recognized on the service member's PEBD month.
//  ****************************************************************************************************
function addPebdCompletedServiceYears(careerMonths, pebd, retSepDate) {
  let serviceYear = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.pebdCompletedServiceYears = 0;
    if (calendarDate.isAfter(pebd) && calendarDate.isSameOrBefore(retSepDate) && calendarMonth === pebd.month() + 1) {
      careerMonth.pebdCompletedServiceYears = serviceYear;
      serviceYear += 1;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Active Service Date (ASD) ~ offset from PEBD when the service member commences active duty.
//    Add the service member's service year/month to each record in the array.
//
//    The common nomenclature for when service member's speak about their career is to state
//    "I am currently in my 1st year of service, 2nd month". The equates to service year "1Y2M"
//    This actually translates to being the service member being in their 2nd month of duty having
//    completed 0 years and only 1 month.
//  ****************************************************************************************************
function addAsdServiceYearsAndMonths(careerMonths, asd, retSepDate) {
  let serviceYear = 1;
  let serviceMonth = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.asdServiceYear = -1;
    careerMonth.asdServiceMonth = -1;
    if (calendarDate.isSameOrAfter(asd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.asdServiceYear = serviceYear;
      careerMonth.asdServiceMonth = serviceMonth;

      if (serviceMonth === 12) {
        serviceYear += 1;
        serviceMonth = 1;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Active Date (ASD) ~ offset from PEBD when the service member commences active duty.
//    Add the service member's present service year/month to each record in the array.
//
//    This translates to the service member's completed years of service and the current month of duty.
//    2Y4M translates to the service member having completed 2 years of service and currently in their
//    4th month of duty. But they have not completed the 4th month of duty yet.
//  ****************************************************************************************************
function addAsdPresentServiceYearsAndMonths(careerMonths, asd, retSepDate) {
  let serviceYear = 0;
  let serviceMonth = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.asdPresentServiceYear = -1;
    careerMonth.asdPresentServiceMonth = -1;
    if (calendarDate.isSameOrAfter(asd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.asdPresentServiceYear = serviceYear;
      careerMonth.asdPresentServiceMonth = serviceMonth;

      if (serviceMonth === 12) {
        serviceYear += 1;
        serviceMonth = 1;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Active Service Date (ASD) ~ offset from PEBD when the service member commences active duty.
//    Add the service member's current service year/month to each record in the array.
//
//    This translates to the service member's completed years and months of service.
//    2Y4M translates to the service member having completed 2 years and 4 months of duty.
//  ****************************************************************************************************
function addAsdCompletedServiceYearsAndMonths(careerMonths, asd, retSepDate) {
  let serviceYear = 0;
  let serviceMonth = 0;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.asdCompletedServiceYear = -1;
    careerMonth.asdCompletedServiceMonth = -1;
    if (calendarDate.isSameOrAfter(asd) && calendarDate.isSameOrBefore(retSepDate)) {
      careerMonth.asdCompletedServiceYear = serviceYear;
      careerMonth.asdCompletedServiceMonth = serviceMonth;

      if (serviceMonth === 11) {
        serviceYear += 1;
        serviceMonth = 0;
      } else {
        serviceMonth += 1;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Active Service Date (ASD) ~ offset from PEBD when the service member commences active duty.
//    Add the service member's completed active service years. 12 months of completed duty = 1
//    service year.
//
//    This translates to the service member's completed years of service.
//    3Y translates to the service member completing a full 3 years (or 36 months) of service.
//    Completed service years are recognized on the service member's ASD month.
//  ****************************************************************************************************
function addAsdCompletedServiceYears(careerMonths, asd, retSepDate) {
  let serviceYear = 1;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.asdCompletedServiceYears = 0;
    if (calendarDate.isAfter(asd) && calendarDate.isSameOrBefore(retSepDate) && calendarMonth === asd.month() + 1) {
      careerMonth.asdCompletedServiceYears = serviceYear;
      serviceYear += 1;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the suggested rank based on year of service (YOS) completed.
//
//    Based on the GlobalSettings-->CareerProgression and the service member's YOS completed,
//    add the associated rank the service member "should" currently possess. This is the government's
//    suggestive career path for a service member and determines if the service member is
//    under performing, on track or over performing.
//  ****************************************************************************************************
function addSuggestedCareerProgression(careerMonths, serviceMember, pebd, retSepDate) {
  const pebdPlus6Months = moment(pebd).add(5, 'months');

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.suggestedRank = '';

    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(pebdPlus6Months)) {
        careerMonth.suggestedRank = 'E-1';
      } else {
        careerMonth.suggestedRank = serviceMember.yearOfServiceSuggestedRank(Number(careerMonth.pebdServiceYear));
      }
    }
  });

  return careerMonths;
}

function addOverrideCareerProgression(careerMonths, serviceMember, rundate, pebd, retSepDate) {
  if (serviceMember.isEligibleForRetirementPension()) {
    //  If less than 36 months remain in service member's career, populate past calendar months to ensure we obtain 36 months.
    if (serviceMember.numberOfMonthsRemainingInServiceCareer() < 36 && serviceMember.careerProgression.hi3PastYears && serviceMember.careerProgression.hi3PastYears.length > 0) {
      //  Retrieve the last year of service in the career progression array. May need to know what the override grade is for past months in the current run date year.
      const objCareerProgressionLastYearOfService = serviceMember.careerProgression.careerService[serviceMember.careerProgression.careerService.length - 1];

      serviceMember.careerProgression.hi3PastYears.forEach((hi3PastYear) => {
        let yearOfServiceCareerMonths = careerMonths.filter((o) => o.pebdServiceYear === hi3PastYear.yearOfService);

        //  The service member is allowed to change their pay grade for the beginning of the current run date year up to one month before the run date.
        if (hi3PastYear.calendarYear === rundate.year()) {
          yearOfServiceCareerMonths = yearOfServiceCareerMonths.filter((o) => o.calendarMonth < rundate.month() + 1);
        }

        yearOfServiceCareerMonths.forEach((yearOfServiceCareerMonth) => {
          yearOfServiceCareerMonth.overrideRank = hi3PastYear.yearOfServiceRank;

          //  The service year in the current date may be split among different ranks.
          if (yearOfServiceCareerMonth.calendarYear === rundate.year()) {
            if (yearOfServiceCareerMonth.calendarMonth >= pebd.month() + 1) {
              yearOfServiceCareerMonth.overrideRank = objCareerProgressionLastYearOfService.yearOfServiceRank;
            }
          }
        });
      });
    }
  }

  //  Populate override rank from run date through ret/sep date.
  const pebdPlus6Months = moment(pebd).add(5, 'months');
  const pebdPlus12Months = moment(pebd).add(11, 'months');

  //  Get run date override rank from Career Progression.
  let objCareerService = serviceMember.careerProgression.careerService.find((o) => o.calendarYear === rundate.year());

  //  For scenarios where the service member's last month in service is in the run date year and the PEBD month has not yet passed,
  //  need to pull the rank from the last year of service in Career Progression.
  if (!objCareerService) {
    objCareerService = serviceMember.careerProgression.careerService.find((o) => o.calendarYear == retSepDate.year() - 1);
  }

  let { yearOfServiceRank } = objCareerService;

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    if (calendarDate.isSameOrAfter(rundate) && calendarDate.isSameOrBefore(retSepDate)) {
      if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(pebdPlus6Months)) {
        yearOfServiceRank = 'E-1';
      } else if (serviceMember.currentRank === 'E-1' && calendarDate.isAfter(pebdPlus6Months) && calendarDate.isSameOrBefore(pebdPlus12Months)) {
        yearOfServiceRank = 'E-2';
      }

      //  Service members are promotoed on their PEBD month.
      if (calendarMonth === pebd.month() + 1) {
        //  Get override rank from Career Progression based on calendar year.
        objCareerService = serviceMember.careerProgression.careerService.find((o) => o.calendarYear === calendarDate.year());

        //  If the last service year is split between two (2) calendar years, the retSep year will NOT be displayed in
        //  Career Progression. Ensure the retSep calendar year months are set to the rank from the first month of the retSep service year.
        if (!objCareerService) {
          objCareerService = serviceMember.careerProgression.careerService[serviceMember.careerProgression.careerService.length - 1];
        }

        ({ yearOfServiceRank } = objCareerService);
      }

      careerMonth.overrideRank = yearOfServiceRank;
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the suggested rank based on year of service (YOS) completed.
//
//    Based on the GlobalSettings-->CareerProgression and the service member's YOS completed,
//    add the associated rank the service member "should" currently possess. This is the government's
//    suggestive career path for a service member and determines if the service member is
//    under performing, on track or over performing.
//  ****************************************************************************************************
function addComponent(careerMonths, serviceMember, pebd, retSepDate) {
  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.component = '';

    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      if (serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.AC_2_AC) {
        careerMonth.component = 'AC';
      } else if (serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.AC_2_RC) {
        careerMonth.component = 'AC';
        if (calendarDate.isSameOrAfter(serviceMember.transferToReserveComponentDate())) {
          careerMonth.component = 'RC';
        }
      } else if (serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.RC_2_AC) {
        careerMonth.component = 'RC';
        if (calendarDate.isSameOrAfter(serviceMember.transferToActiveComponentDate())) {
          careerMonth.component = 'AC';
        }
      } else if (serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.RC_2_RC) {
        careerMonth.component = 'RC';
      } else {
        careerMonth.component = 'error';
      }
    }
  });

  return careerMonths;
}

export function getPayTableMonthlyPay(payTables, payTableFilename, payGrade, rank, pebdserviceYear) {
  let payTableServiceYear = -1;

  if (pebdserviceYear < 2) payTableServiceYear = 'Under 2';
  if (pebdserviceYear >= 2) payTableServiceYear = 2;
  if (pebdserviceYear >= 3) payTableServiceYear = 3;
  if (pebdserviceYear >= 4) payTableServiceYear = 4;
  if (pebdserviceYear >= 6) payTableServiceYear = 6;
  if (pebdserviceYear >= 8) payTableServiceYear = 8;
  if (pebdserviceYear >= 10) payTableServiceYear = 10;
  if (pebdserviceYear >= 12) payTableServiceYear = 12;
  if (pebdserviceYear >= 14) payTableServiceYear = 14;
  if (pebdserviceYear >= 16) payTableServiceYear = 16;
  if (pebdserviceYear >= 18) payTableServiceYear = 18;
  if (pebdserviceYear >= 20) payTableServiceYear = 20;
  if (pebdserviceYear >= 22) payTableServiceYear = 22;
  if (pebdserviceYear >= 24) payTableServiceYear = 24;
  if (pebdserviceYear >= 26) payTableServiceYear = 26;
  if (pebdserviceYear >= 28) payTableServiceYear = 28;
  if (pebdserviceYear >= 30) payTableServiceYear = 30;
  if (pebdserviceYear >= 32) payTableServiceYear = 32;
  if (pebdserviceYear >= 34) payTableServiceYear = 34;
  if (pebdserviceYear >= 36) payTableServiceYear = 36;
  if (pebdserviceYear >= 38) payTableServiceYear = 38;
  if (pebdserviceYear >= 40) payTableServiceYear = 40;

  return payTables[payTableFilename][payGrade][rank][payTableServiceYear];
}

//  ****************************************************************************************************
//    Add the current monthly pay amount (Today's $) to each record in the array based on years of
//    service completed and rank.
//
//    For records prior to the run date year/month, the GlobalSettings-->PayTables are utilized. If
//    the year/month are older than the oldest pay table, the oldest pay table is utilized.
//
//    The current monthly pay amount (Today's $) is based on the years of completed service and rank.
//  ****************************************************************************************************
function addPayAmountTodaysDollars(careerMonths, serviceMember, rundate, pebd, retSepDate) {
  const { payTables } = serviceMember;
  const pebdPlus3Months = moment(pebd).add(2, 'months');
  const pebdPlus4Months = moment(pebd).add(3, 'months');
  const pebdPlus6Months = moment(pebd).add(5, 'months');
  let rank = '';
  let pebdserviceYear = 0;
  let payGrade = '';

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  When a non-regular retirement pension is being implemented, the "Pay Todays $" must be projected thru the month before the first tsp payout.
    //  When a regular retirement pension is begin implemented, only need record up to and including the retSep date. But don't know what the
    //  retirement type is at this point, so just extend the pay amounts out to 1 month prior to TSP being paid out.
    const endDate = moment(serviceMember.tspWithdrawalDate()).subtract(1, 'months');

    careerMonth.payAmountTodaysDollars = 0;
    if (calendarDate.isSameOrAfter(rundate) && calendarDate.isSameOrBefore(endDate)) {
      //  Determine rank based on the calendar date.
      //    1.  If the calendar date is within the 1st three months of career, make E-1<4 (in boot camp, lower pay than regular E-1 rank).
      //    2.  if the calendar date is within month 4 thru 6, make E-1 (pay increase from completing boot camp).
      //    3.  After retSepDate, rank will retain to the last rank obtained while in service (required to determine TSP payment for non-regular retirement).
      if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(pebdPlus3Months)) {
        rank = 'E-1<4';
      } else if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebdPlus4Months) && calendarDate.isSameOrBefore(pebdPlus6Months)) {
        rank = 'E-1';
      } else if (calendarDate.isSameOrAfter(rundate) && calendarDate.isSameOrBefore(retSepDate)) {
        rank = careerMonth.overrideRank;
      }

      pebdserviceYear = calendarDate.isSameOrBefore(retSepDate) ? careerMonth.pebdServiceYear : pebdserviceYear;

      //  Non-regular retirement requires the 'Pay Today/Future $' to be projected up to 1 month prior to the non-regular retirement withdrawal age.
      pebdserviceYear = (calendarDate.isAfter(retSepDate) && calendarMonth === pebd.month() + 1) ? pebdserviceYear + 1 : pebdserviceYear;

      payGrade = calendarDate.isSameOrBefore(retSepDate) ? serviceMember.currentPayGrade(rank) : payGrade;

      let yearsDifferenceCalendarDateFromRundate = calendarYear > rundate.year() ? 0 : rundate.year() - calendarYear;
      yearsDifferenceCalendarDateFromRundate = yearsDifferenceCalendarDateFromRundate > 3 ? 3 : yearsDifferenceCalendarDateFromRundate; //  3 years in past is max.
      const payTableFilename = `runDateMinus${yearsDifferenceCalendarDateFromRundate}${(yearsDifferenceCalendarDateFromRundate === 1 ? 'Year' : 'Years')}`;

      careerMonth.payAmountTodaysDollars = getPayTableMonthlyPay(payTables, payTableFilename, payGrade, rank, pebdserviceYear);
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the current monthly pay amount (Future $) to each record in the array.
//
//    For records equal to the run date year, the pay amount is equal to the today's dollar pay amount.
//
//    For records greater than the run date year, the associated GlobalSettings-->PayTableGrowthRate
//    multiplier is used.
//    Example:
//      1.  Run date year is 2020
//      2.  Future dollay current pay amount = today's current pay amount.
//      3.  In 2021, the current pay amount (Today's $) is multiplied/added by
//          GlobalSettings-->PayTableGrowthRates-->Year1.
//      4.  In 2022, the current pay amount (Today's $) is multiplied/added by
//          GlobalSettings-->PayTableGrowthRates-->Year1 as well ass
//          GlobalSettings-->PayTableGrowthRates-- > Year2.
//  ****************************************************************************************************
function addPayAmountFutureDollars(careerMonths, serviceMember, rundate) {
  const payTableGrowthRateYear1 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year1) / 100 + 1;
  const payTableGrowthRateYear2 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year2) / 100 + 1;
  const payTableGrowthRateYear3 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year3) / 100 + 1;
  const payTableGrowthRateYear4 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year4) / 100 + 1;
  const payTableGrowthRateYear5 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year5) / 100 + 1;

  for (let index = 0; index <= careerMonths.length - 1; index += 1) {
    const careerMonth = careerMonths[index];

    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  When a non-regular retirement pension is being implemented, the "Pay Future $" must be projected thru the month before the first tsp payout.
    //  When a regular retirement pension is begin implemented, only need record up to and including the retSep date. But don't know what the
    //  retirement type is at this point, so just extend the pay amounts out to 1 month prior to TSP being paid out.
    const endDate = moment(serviceMember.tspWithdrawalDate()).subtract(1, 'months');

    careerMonth.payAmountFutureDollars = 0;

    if (calendarDate.isSameOrAfter(rundate) && calendarDate.isSameOrBefore(endDate)) {
      let payAmountFutureDollars = 0;
      const payAmountTodaysDollars = toNumber(careerMonth.payAmountTodaysDollars);

      if (calendarYear === rundate.year()) {
        //  No adjustment for 1st calendar year.
        payAmountFutureDollars = payAmountTodaysDollars;
      } else if (calendarYear >= (rundate.year() + 1)) {
        //  Adjust for inflation.
        const calendarYearFromRundateYearOffset = calendarYear - rundate.year();

        payAmountFutureDollars = payAmountTodaysDollars * payTableGrowthRateYear1;
        if (calendarYearFromRundateYearOffset >= 2) payAmountFutureDollars *= payTableGrowthRateYear2;
        if (calendarYearFromRundateYearOffset >= 3) payAmountFutureDollars *= payTableGrowthRateYear3;
        if (calendarYearFromRundateYearOffset >= 4) payAmountFutureDollars *= payTableGrowthRateYear4;
        if (calendarYearFromRundateYearOffset >= 5) payAmountFutureDollars *= payTableGrowthRateYear5;
        if (calendarYearFromRundateYearOffset >= 6) {
          for (let yearOffset = 6; yearOffset <= calendarYearFromRundateYearOffset; yearOffset += 1) {
            payAmountFutureDollars *= payTableGrowthRateYear5;
          }
        }
      }

      careerMonth.payAmountFutureDollars = payAmountFutureDollars;
    }
  }

  return careerMonths;
}

//  ****************************************************************************************************
//    Set the "Pay Amount (Today's $)" equal to the "Current Pay (Today's $)" while the service member
//    is in service.
//  ****************************************************************************************************
function addCurrentPayAmountTodaysDollars(careerMonths, pebd, retSepDate) {
  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.currentPayAmountTodaysDollars = 0;
    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      if (careerMonth.component === 'AC') {
        careerMonth.currentPayAmountTodaysDollars = careerMonth.payAmountTodaysDollars;
      }
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Set the "Pay Amount (Future $)" equal to the "Current Pay (Future $)" while the service member
//    is in service.
//  ****************************************************************************************************
function addCurrentPayAmountFutureDollars(careerMonths, pebd, retSepDate) {
  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.currentPayAmountFutureDollars = 0;
    if (calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(retSepDate)) {
      if (careerMonth.component === 'AC') {
        careerMonth.currentPayAmountFutureDollars = careerMonth.payAmountFutureDollars;
      }
    }
  });

  return careerMonths;
}

export function pmt(totalNumberOfPayments, rate, loanAmount) {
  if (rate === 0) return loanAmount / totalNumberOfPayments;

  const denominator = 1 - Math.pow((1 + rate), totalNumberOfPayments * -1);
  return (loanAmount * rate) / denominator;
}

export function getReducedContributionAmountIfIrsCapIsMet(currentAmount, contributionAmount, capAmount) {
  let irsTspCapLimitMet = false;

  if (currentAmount + contributionAmount >= capAmount) {
    contributionAmount = capAmount - currentAmount;
    irsTspCapLimitMet = true;
  }
  return { irsTspCapLimitMet, contributionAmount };
}

function computeTspIrsYearlyCapAmounts(serviceMember, allNumbersByMonth, rundate, retSepDate) {
  const { tspIrsYearlyCapGrowth } = serviceMember;
  let tspIrsYearlyCapLimitFutureDollars = serviceMember.tspIrsYearlyCapLimit;

  allNumbersByMonth.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  Set the TSP IRS cap limit for the rundate and each subsequent month of January.
    if (calendarDate.isSame(rundate)) {
      careerMonth.tspIrsYearlyCapLimitTodaysDollars = serviceMember.tspIrsYearlyCapLimit;
      careerMonth.tspIrsYearlyCapLimitFutureDollars = serviceMember.tspIrsYearlyCapLimit;
    } else if (calendarDate.isAfter(rundate) && calendarDate.isSameOrBefore(retSepDate) && calendarDate.month() + 1 === 1) {
      careerMonth.tspIrsYearlyCapLimitTodaysDollars = serviceMember.tspIrsYearlyCapLimit;

      tspIrsYearlyCapLimitFutureDollars += tspIrsYearlyCapGrowth;
      careerMonth.tspIrsYearlyCapLimitFutureDollars = tspIrsYearlyCapLimitFutureDollars;
    }
  });

  return allNumbersByMonth;
}

function computeServiceMemberTspContribution(serviceMember, allNumbersByMonth, tspStartDate, retSepDate) {

  const tspServiceMemberContributionRate = serviceMember.tspServiceMemberContributionRate / 100;

  allNumbersByMonth.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  Compute the monthly TSP contribution:
    //    1.  Calculate TSP contribution amount.
    //    2.  Retrieve the calendar year IRS TSP cap limit.
    //    3.  Retrieve the prior calendar year's yearly running total. The amount resets every January.
    //    4.  Determine if adding the current month's contribution amount will exceed the IRS TSP cap limit. If it does, adjust the amount to max out the contrinbution to the limit.
    //    5.  Add the monthly contribution amount to the monthly total.
    //    6.  Add this month's monthly total contribution amount to the yearly amount.
    if (calendarDate.isSameOrAfter(tspStartDate) && calendarDate.isSameOrBefore(retSepDate)) {
      //  Today's dollars.
      careerMonth.tspServiceMemberContributionAmountTodaysDollars = careerMonth.currentPayAmountTodaysDollars * tspServiceMemberContributionRate; // 1
      const { tspIrsYearlyCapLimitTodaysDollars } = allNumbersByMonth.find((x) => x.calendarYear === careerMonth.calendarYear && x.tspIrsYearlyCapLimitTodaysDollars > 0); // 2
      const priorTspYearlyRunningTotalAmountTodaysDollars = calendarDate.isSame(tspStartDate) || calendarMonth === 1 ? 0 : allNumbersByMonth[index - 1].tspYearlyRunningTotalAmountTodaysDollars; // 3
      const irsTspCapLimitMetDataTodaysDollars = getReducedContributionAmountIfIrsCapIsMet(priorTspYearlyRunningTotalAmountTodaysDollars, careerMonth.tspServiceMemberContributionAmountTodaysDollars, tspIrsYearlyCapLimitTodaysDollars); // 4
      careerMonth.irsTspCapLimitMetTodaysDollars = irsTspCapLimitMetDataTodaysDollars.irsTspCapLimitMet;
      careerMonth.tspServiceMemberContributionAmountTodaysDollars = irsTspCapLimitMetDataTodaysDollars.contributionAmount;
      careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars = careerMonth.tspServiceMemberContributionAmountTodaysDollars; // 5
      careerMonth.tspYearlyRunningTotalAmountTodaysDollars = priorTspYearlyRunningTotalAmountTodaysDollars + careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars; // 6

      //  Future dollars.
      careerMonth.tspServiceMemberContributionAmountFutureDollars = careerMonth.currentPayAmountFutureDollars * tspServiceMemberContributionRate; // 1
      const { tspIrsYearlyCapLimitFutureDollars } = allNumbersByMonth.find((x) => x.calendarYear === careerMonth.calendarYear && x.tspIrsYearlyCapLimitFutureDollars > 0); // 2
      const priorTspYearlyRunningTotalAmountFutureDollars = calendarDate.isSame(tspStartDate) || calendarMonth === 1 ? 0 : allNumbersByMonth[index - 1].tspYearlyRunningTotalAmountFutureDollars; // 3
      const irsTspCapLimitMetDataFutureDollars = getReducedContributionAmountIfIrsCapIsMet(priorTspYearlyRunningTotalAmountFutureDollars, careerMonth.tspServiceMemberContributionAmountFutureDollars, tspIrsYearlyCapLimitFutureDollars); // 4
      careerMonth.irsTspCapLimitMetFutureDollars = irsTspCapLimitMetDataFutureDollars.irsTspCapLimitMet;
      careerMonth.tspServiceMemberContributionAmountFutureDollars = irsTspCapLimitMetDataFutureDollars.contributionAmount;
      careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars = careerMonth.tspServiceMemberContributionAmountFutureDollars; // 5
      careerMonth.tspYearlyRunningTotalAmountFutureDollars = priorTspYearlyRunningTotalAmountFutureDollars + careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars; // 6
    }
  });

  return allNumbersByMonth;
}

function generateServiceMemberTspBalance(serviceMember, allNumbersByMonth, rundate, retSepDate, tspWithdrawalDate, endOfLifeDate) {
  const tspInflationRate = serviceMember.globalSettings.tspSettings.inflationRate / 100;
  const tspGrowthRate = serviceMember.tspGrowthRate / 100;
  const tspWithdrawalGrowthRate = serviceMember.tspWithdrawalGrowthRate / 100;

  //  Calculate growth rates for run date thru withdrawal date (minus 1 month).
  const tspMonthlyGrowthRate = Math.pow(1 + tspGrowthRate, 1 / 12) - 1;
  const tspGrowthRateWithInflation = (1 + tspGrowthRate) / (1 + tspInflationRate) - 1;
  const tspMonthlyGrowthRateWithInflation = Math.pow((1 + tspGrowthRateWithInflation), 1 / 12) - 1;

  //  Calculate growth rates for withdrawal date thru death date.
  const tspWithdrawalMonthlyGrowthRate = Math.pow(1 + tspWithdrawalGrowthRate, 1 / 12) - 1;
  const tspWithdrawalGrowthRateWithInflation = (1 + tspWithdrawalGrowthRate) / (1 + tspInflationRate) - 1;
  const tspWithdrawalMonthlyGrowthRateWithInflation = Math.pow((1 + tspWithdrawalGrowthRateWithInflation), 1 / 12) - 1;

  allNumbersByMonth.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  No growth amount in run date.
    if (calendarDate.isSame(rundate)) {
      careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars = careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars;
      careerMonth.tspServiceMemberGrowthAmountTodaysDollars = 0;
      careerMonth.tspServiceMemberBalanceTodaysDollars = careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars + serviceMember.tspServiceMemberContribution;

      careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars = careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars;
      careerMonth.tspServiceMemberGrowthAmountFutureDollars = 0;
      careerMonth.tspServiceMemberBalanceFutureDollars = careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars + serviceMember.tspServiceMemberContribution;
    } else if (calendarDate.isAfter(rundate)) {
      careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceTodaysDollars + careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars;
      careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceFutureDollars + careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars;

      if (calendarDate.isAfter(rundate) && calendarDate.isBefore(tspWithdrawalDate)) {
        careerMonth.tspServiceMemberGrowthAmountTodaysDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceTodaysDollars * tspMonthlyGrowthRateWithInflation;
        careerMonth.tspServiceMemberGrowthAmountFutureDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceFutureDollars * tspMonthlyGrowthRate;
      } else if (calendarDate.isSameOrAfter(tspWithdrawalDate) && calendarDate.isSameOrBefore(endOfLifeDate)) {
        careerMonth.tspServiceMemberGrowthAmountTodaysDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceTodaysDollars * tspWithdrawalMonthlyGrowthRateWithInflation;
        careerMonth.tspServiceMemberGrowthAmountFutureDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceFutureDollars * tspWithdrawalMonthlyGrowthRate;
      }

      careerMonth.tspServiceMemberBalanceTodaysDollars = careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars + careerMonth.tspServiceMemberGrowthAmountTodaysDollars;
      careerMonth.tspServiceMemberBalanceFutureDollars = careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars + careerMonth.tspServiceMemberGrowthAmountFutureDollars;
    }
  });

  return allNumbersByMonth;
}

function generateServiceMemberTspWithdrawal(serviceMember, allNumbersByMonth, tspWithdrawalDate, endOfLifeDate) {
  const tspInflationRate = serviceMember.globalSettings.tspSettings.inflationRate / 100;

  let tspServiceMemberWithdrawalTodaysDollars = 0;
  let tspServiceMemberWithdrawalFutureDollars = 0;

  //  Calculate growth rates for run date thru withdrawal date (minus 1 month).
  const tspGrowthRate = serviceMember.tspGrowthRate / 100;
  // const tspMonthlyGrowthRate = Math.pow(1 + tspGrowthRate, 1 / 12) - 1; // NOSONAR
  const tspGrowthRateWithInflation = (1 + tspGrowthRate) / (1 + tspInflationRate) - 1;
  const tspMonthlyGrowthRateWithInflation = Math.pow((1 + tspGrowthRateWithInflation), 1 / 12) - 1;

  //  Calculate growth rates for withdrawal date thru death date.
  const tspWithdrawalGrowthRate = serviceMember.tspWithdrawalGrowthRate / 100;
  const tspWithdrawalMonthlyGrowthRate = Math.pow(1 + tspWithdrawalGrowthRate, 1 / 12) - 1;
  const tspWithdrawalGrowthRateWithInflation = (1 + tspWithdrawalGrowthRate) / (1 + tspInflationRate) - 1;
  const tspWithdrawalMonthlyGrowthRateWithInflation = Math.pow((1 + tspWithdrawalGrowthRateWithInflation), 1 / 12) - 1;

  //  Get 1 month prior to the withdrawal date when the service member TSP balance is at its highest.
  const withdrawalDateMinus1Month = moment(tspWithdrawalDate).subtract(1, 'months');
  const careerMonthAtWithdrawalDateMinus1Month = allNumbersByMonth.find((x) => x.calendarYear === withdrawalDateMinus1Month.year() && x.calendarMonth === withdrawalDateMinus1Month.month() + 1);

  //  Compute service member TSP monthly payment.
  if (!isUndefined(careerMonthAtWithdrawalDateMinus1Month)) {
    const tspNumberOfPayments = serviceMember.numberOfMonthsFromTspWithdrawalDateToDeathDate();

    //  Compute monthly TSP payment amount based on whether the growth rate is 0 or not. This is a divide by 0 check.
    if (tspMonthlyGrowthRateWithInflation !== 0) {
      tspServiceMemberWithdrawalTodaysDollars = pmt(tspNumberOfPayments, tspWithdrawalMonthlyGrowthRateWithInflation, careerMonthAtWithdrawalDateMinus1Month.tspServiceMemberBalanceTodaysDollars);
    } else {
      tspServiceMemberWithdrawalTodaysDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceTodaysDollars / tspNumberOfPayments;
    }

    if (tspMonthlyGrowthRateWithInflation !== 0) {
      tspServiceMemberWithdrawalFutureDollars = pmt(tspNumberOfPayments, tspWithdrawalMonthlyGrowthRate, careerMonthAtWithdrawalDateMinus1Month.tspServiceMemberBalanceFutureDollars);
    } else {
      tspServiceMemberWithdrawalFutureDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceFutureDollars / tspNumberOfPayments;
    }

    allNumbersByMonth.forEach((careerMonth, index) => {
      const calendarYear = Number(careerMonth.calendarYear);
      const calendarMonth = Number(careerMonth.calendarMonth);
      const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

      if (calendarDate.isSameOrAfter(tspWithdrawalDate) && calendarDate.isSameOrBefore(endOfLifeDate)) {
        careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceTodaysDollars;
        careerMonth.tspServiceMemberGrowthAmountTodaysDollars = careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars * tspWithdrawalMonthlyGrowthRateWithInflation;
        careerMonth.tspServiceMemberWithdrawalTodaysDollars = tspServiceMemberWithdrawalTodaysDollars;
        careerMonth.tspServiceMemberBalanceTodaysDollars = careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars + careerMonth.tspServiceMemberGrowthAmountTodaysDollars - tspServiceMemberWithdrawalTodaysDollars;

        careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars = allNumbersByMonth[index - 1].tspServiceMemberBalanceFutureDollars;
        careerMonth.tspServiceMemberGrowthAmountFutureDollars = careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars * tspWithdrawalMonthlyGrowthRate;
        careerMonth.tspServiceMemberWithdrawalFutureDollars = tspServiceMemberWithdrawalFutureDollars;
        careerMonth.tspServiceMemberBalanceFutureDollars = careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars + careerMonth.tspServiceMemberGrowthAmountFutureDollars - tspServiceMemberWithdrawalFutureDollars;
      }
    });
  }

  return allNumbersByMonth;
}

export function generateServiceMemberTsp(serviceMember, isEligibleForBonus) {
  let { allNumbersByMonth } = serviceMember.results;
  const rundate = serviceMember.rundate();
  const pebd = serviceMember.pebd();
  const retSepDate = serviceMember.retSepDate();
  const tspWithdrawalDate = serviceMember.tspWithdrawalDate();
  const endOfLifeDate = serviceMember.endOfLifeDate();

  //  If BRSC calculator, TSP starts on the opt-in month/year.
  let tspStartDate = rundate;
  if (serviceMember.moduleSettings.details.calculatorName === 'BRS Comparison') {
    tspStartDate = moment([serviceMember.optInYear, serviceMember.optInMonth - 1, 1]);
  }

  allNumbersByMonth = computeTspIrsYearlyCapAmounts(serviceMember, allNumbersByMonth, rundate, retSepDate);
  allNumbersByMonth = computeServiceMemberTspContribution(serviceMember, allNumbersByMonth, tspStartDate, retSepDate);

  if (serviceMember.moduleSettings.details.calculatorName === 'REDUX') {
    const { numberOfInstallments, percentageToTsp } = serviceMember;

    allNumbersByMonth = computeCareerServiceBonusTodaysDollars(allNumbersByMonth, rundate, pebd, retSepDate, numberOfInstallments, percentageToTsp);
    allNumbersByMonth = computeCareerServiceBonusFutureDollars(allNumbersByMonth, rundate, pebd, retSepDate, numberOfInstallments, percentageToTsp);
  } else if (serviceMember.moduleSettings.details.calculatorName === 'BRS' || serviceMember.moduleSettings.details.calculatorName === 'BRS Comparison') {
    const {
      completionMultiplier,
      addBonusTo1stMonthAfterCompletingYos,
      serviceAgreementYears,
      numberOfInstallments,
      percentageToTsp,
      payEntryBaseDateMonth,
    } = serviceMember;

    //  Need to compute Continuation Pay in the event part of it goes into TSP.
    allNumbersByMonth = computeContinuationPayTodaysDollars(serviceMember, allNumbersByMonth, pebd, rundate, retSepDate, completionMultiplier, addBonusTo1stMonthAfterCompletingYos, serviceAgreementYears, numberOfInstallments, percentageToTsp, payEntryBaseDateMonth, isEligibleForBonus);
    allNumbersByMonth = computeContinuationPayFutureDollars(serviceMember, allNumbersByMonth, pebd, rundate, retSepDate, completionMultiplier, addBonusTo1stMonthAfterCompletingYos, serviceAgreementYears, numberOfInstallments, percentageToTsp, payEntryBaseDateMonth, isEligibleForBonus); // NOSONAR
  }

  allNumbersByMonth = generateServiceMemberTspBalance(serviceMember, allNumbersByMonth, rundate, retSepDate, tspWithdrawalDate, endOfLifeDate);
  allNumbersByMonth = generateServiceMemberTspWithdrawal(serviceMember, allNumbersByMonth, tspWithdrawalDate, endOfLifeDate);

  return allNumbersByMonth;
}

export function initializeValues(serviceMember) {
  const { allNumbersByMonth } = serviceMember.results;

  allNumbersByMonth.forEach((careerMonth) => {
    careerMonth.bonusAmountTodaysDollars = 0;
    careerMonth.bonusAmountFutureDollars = 0;

    careerMonth.bonusAmountToTspTodaysDollars = 0;
    careerMonth.bonusAmountToTspFutureDollars = 0;

    careerMonth.tspGovernmentMandatoryContributionAmountTodaysDollars = 0;
    careerMonth.tspGovernmentMandatoryContributionAmountFutureDollars = 0;

    careerMonth.tspGovernmentMatchingContributionAmountTodaysDollars = 0;
    careerMonth.tspGovernmentMatchingContributionAmountFutureDollars = 0;

    careerMonth.tspGovernmentContributionMonthlyTotalTodaysDollars = 0;
    careerMonth.tspGovernmentContributionMonthlyTotalFutureDollars = 0;

    careerMonth.tspGovernmentBalanceNoGrowthTodaysDollars = 0;
    careerMonth.tspGovernmentBalanceNoGrowthFutureDollars = 0;

    careerMonth.tspGovernmentGrowthAmountTodaysDollars = 0;
    careerMonth.tspGovernmentGrowthAmountFutureDollars = 0;

    careerMonth.tspGovernmentWithdrawalTodaysDollars = 0;
    careerMonth.tspGovernmentWithdrawalFutureDollars = 0;

    careerMonth.tspGovernmentBalanceTodaysDollars = 0;
    careerMonth.tspGovernmentBalanceFutureDollars = 0;

    careerMonth.tspServiceMemberBalanceTodaysDollars = 0;
    careerMonth.tspServiceMemberBalanceFutureDollars = 0;

    careerMonth.tspServiceMemberContributionAmountTodaysDollars = 0;
    careerMonth.tspServiceMemberContributionAmountFutureDollars = 0;

    careerMonth.tspServiceMemberContributionMonthlyTotalTodaysDollars = 0;
    careerMonth.tspServiceMemberContributionMonthlyTotalFutureDollars = 0;

    careerMonth.tspServiceMemberBalanceNoGrowthTodaysDollars = 0;
    careerMonth.tspServiceMemberBalanceNoGrowthFutureDollars = 0;

    careerMonth.tspYearlyRunningTotalAmountTodaysDollars = 0;
    careerMonth.tspYearlyRunningTotalAmountFutureDollars = 0;

    careerMonth.tspServiceMemberWithdrawalTodaysDollars = 0;
    careerMonth.tspServiceMemberWithdrawalFutureDollars = 0;

    careerMonth.tspServiceMemberGrowthAmountTodaysDollars = 0;
    careerMonth.tspServiceMemberGrowthAmountFutureDollars = 0;

    careerMonth.tspIrsYearlyCapLimitTodaysDollars = 0;
    careerMonth.tspIrsYearlyCapLimitFutureDollars = 0;

    careerMonth.lumpSumTwentyFivePercentPensionAmountTodaysDollars = 0;
    careerMonth.lumpSumTwentyFivePercentPensionAmountFutureDollars = 0;

    careerMonth.lumpSumFiftyPercentPensionAmountTodaysDollars = 0;
    careerMonth.lumpSumFiftyPercentPensionAmountFutureDollars = 0;

    careerMonth.lumpSumTwentyFivePercentInstallmentTodaysDollars = 0;
    careerMonth.lumpSumTwentyFivePercentInstallmentFutureDollars = 0;

    careerMonth.lumpSumFiftyPercentInstallmentTodaysDollars = 0;
    careerMonth.lumpSumFiftyPercentInstallmentFutureDollars = 0;
  });

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Formulate and populate data for each month of a service member's career.
//    Only retrieve what is needed to accomodate generating the Career Progression page.
//  ****************************************************************************************************
export function getAllNumbersByMonthForCareerProgression(serviceMember) {
  let allNumbersByMonth = [];

  const dob = serviceMember.dob();
  const pebd = serviceMember.pebd();
  const asd = serviceMember.asd();
  const retSepDate = serviceMember.retSepDate();

  allNumbersByMonth = addRecordId(allNumbersByMonth, serviceMember);
  allNumbersByMonth = addCalendarYearMonth(allNumbersByMonth, dob);
  allNumbersByMonth = addAge(allNumbersByMonth);
  allNumbersByMonth = addAgePresentYears(allNumbersByMonth);
  allNumbersByMonth = addAgeCompletedYears(allNumbersByMonth);

  allNumbersByMonth = addPebdServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = addPebdPresentServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = addPebdCompletedServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = addPebdCompletedServiceYears(allNumbersByMonth, pebd, retSepDate);

  allNumbersByMonth = addAsdServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = addAsdPresentServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = addAsdCompletedServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = addAsdCompletedServiceYears(allNumbersByMonth, asd, retSepDate);

  allNumbersByMonth = addComponent(allNumbersByMonth, serviceMember, pebd, retSepDate);

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Formulate and populate data for each month of a service member's career.
//  ****************************************************************************************************
export function getAllNumbersByMonth(serviceMember, funcOverrides = {}) {
  // these functions can be overwritten
  const defaultFuncs = {
    addRecordId,
    addCalendarYearMonth,
    addAge,
    addAgePresentYears,
    addAgeCompletedYears,
    addPebdServiceYearsAndMonths,
    addPebdPresentServiceYearsAndMonths,
    addPebdCompletedServiceYearsAndMonths,
    addPebdCompletedServiceYears,
    addAsdServiceYearsAndMonths,
    addAsdPresentServiceYearsAndMonths,
    addAsdCompletedServiceYearsAndMonths,
    addAsdCompletedServiceYears,
    addSuggestedCareerProgression,
    addOverrideCareerProgression,
    addComponent,
    addPayAmountTodaysDollars,
    addPayAmountFutureDollars,
    addCurrentPayAmountTodaysDollars,
    addCurrentPayAmountFutureDollars,
  };
  const funcs = { ...defaultFuncs, ...funcOverrides };

  let allNumbersByMonth = [];

  const rundate = serviceMember.rundate();
  const dob = serviceMember.dob();
  const pebd = serviceMember.pebd();
  const asd = serviceMember.asd();
  const retSepDate = serviceMember.retSepDate();

  allNumbersByMonth = funcs.addRecordId(allNumbersByMonth, serviceMember);
  allNumbersByMonth = funcs.addCalendarYearMonth(allNumbersByMonth, dob);
  allNumbersByMonth = funcs.addAge(allNumbersByMonth);
  allNumbersByMonth = funcs.addAgePresentYears(allNumbersByMonth);
  allNumbersByMonth = funcs.addAgeCompletedYears(allNumbersByMonth);

  allNumbersByMonth = funcs.addPebdServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = funcs.addPebdPresentServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = funcs.addPebdCompletedServiceYearsAndMonths(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = funcs.addPebdCompletedServiceYears(allNumbersByMonth, pebd, retSepDate);

  allNumbersByMonth = funcs.addAsdServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = funcs.addAsdPresentServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = funcs.addAsdCompletedServiceYearsAndMonths(allNumbersByMonth, asd, retSepDate);
  allNumbersByMonth = funcs.addAsdCompletedServiceYears(allNumbersByMonth, asd, retSepDate);

  allNumbersByMonth = funcs.addSuggestedCareerProgression(allNumbersByMonth, serviceMember, pebd, retSepDate);
  allNumbersByMonth = funcs.addOverrideCareerProgression(allNumbersByMonth, serviceMember, rundate, pebd, retSepDate);
  allNumbersByMonth = funcs.addComponent(allNumbersByMonth, serviceMember, pebd, retSepDate);
  allNumbersByMonth = funcs.addPayAmountTodaysDollars(allNumbersByMonth, serviceMember, rundate, pebd, retSepDate);
  allNumbersByMonth = funcs.addPayAmountFutureDollars(allNumbersByMonth, serviceMember, rundate);
  allNumbersByMonth = funcs.addCurrentPayAmountTodaysDollars(allNumbersByMonth, pebd, retSepDate);
  allNumbersByMonth = funcs.addCurrentPayAmountFutureDollars(allNumbersByMonth, pebd, retSepDate);

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Collect data for each month of a service member's career in Today's Dollars only.
//  ****************************************************************************************************
export function getAllNumbersByMonthFromRunDateToDeathDateTodaysDollars(serviceMember) {
  const firstMonthOutOfService = serviceMember.firstMonthOutOfServiceDate();
  const tspWithdrawalDate = serviceMember.tspWithdrawalDate();
  const allNumbersByMonth = getCommonAllNumbersByMonthFromRunDateToDeathDate(serviceMember);

  allNumbersByMonth.forEach((month) => {
    const calendarYear = Number(month.calendarYear);
    const calendarMonth = Number(month.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    const obj = serviceMember.results.allNumbersByMonth.find((o) => o.recordId === month.recordId);

    month.tspBalance = obj.tspServiceMemberBalanceTodaysDollars;

    if (calendarDate.isBefore(firstMonthOutOfService)) {
      month.payAmount = obj.payAmountTodaysDollars;
      month.currentPayAmount = obj.currentPayAmountTodaysDollars;
      month.tspContributionAmount = obj.tspServiceMemberContributionAmountTodaysDollars;
      month.tspContributionAmountYearlyRunningTotal = obj.tspYearlyRunningTotalAmountTodaysDollars;
      month.tspIrsYearlyCapLimit = obj.tspIrsYearlyCapLimitTodaysDollars;
    }

    if (calendarDate.isSameOrAfter(firstMonthOutOfService)) {
      month.pensionAmountTodaysDollars = obj.pensionAmountTodaysDollars;
    }

    if (calendarDate.isSameOrAfter(tspWithdrawalDate)) {
      month.tspServiceMemberPaymentAmount = obj.tspServiceMemberWithdrawalTodaysDollars;
    }
  });

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Aggregate data for each month of a service member's career in Future Dollars only.
//  ****************************************************************************************************
export function getAllNumbersByMonthFromRunDateToDeathDateFutureDollars(serviceMember) {
  const firstMonthOutOfService = serviceMember.firstMonthOutOfServiceDate();
  const tspWithdrawalDate = serviceMember.tspWithdrawalDate();
  const allNumbersByMonth = getCommonAllNumbersByMonthFromRunDateToDeathDate(serviceMember);

  allNumbersByMonth.forEach((month) => {
    const calendarYear = Number(month.calendarYear);
    const calendarMonth = Number(month.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    const obj = serviceMember.results.allNumbersByMonth.find((o) => o.recordId === month.recordId);

    month.tspBalance = obj.tspServiceMemberBalanceFutureDollars;

    if (calendarDate.isBefore(firstMonthOutOfService)) {
      month.payAmount = obj.payAmountFutureDollars;
      month.currentPayAmount = obj.currentPayAmountFutureDollars;
      month.tspContributionAmount = obj.tspServiceMemberContributionAmountFutureDollars;
      month.tspContributionAmountYearlyRunningTotal = obj.tspYearlyRunningTotalAmountFutureDollars;
      month.tspIrsYearlyCapLimit = obj.tspIrsYearlyCapLimitFutureDollars;
    }

    if (calendarDate.isSameOrAfter(firstMonthOutOfService)) {
      month.pensionAmountFutureDollars = obj.pensionAmountFutureDollars;
    }

    if (calendarDate.isSameOrAfter(tspWithdrawalDate)) {
      month.tspServiceMemberPaymentAmount = obj.tspServiceMemberWithdrawalFutureDollars;
    }
  });

  return allNumbersByMonth;
}

//  ****************************************************************************************************
//    Calculate the Hi3 Pension for a regular retirement.
//    For Today's Dollars, the payout amount is the same for all months starting at the 1st month
//    after leaving the service thru death month.
//  ****************************************************************************************************
export function addHi3PensionPaymentsRegularRetirementTodaysDollars(careerMonths, serviceMember, paymentStartDate, retSepDate) {
  //  Compute active duty service time from 1 month prior to the ret/sep date.
  const paymentStartDateMinus1month = moment(paymentStartDate).subtract(1, 'months');
  const paymentStartDateMinus1monthCareerMonth = careerMonths.find((o) => o.calendarYear === paymentStartDateMinus1month.year() && o.calendarMonth === paymentStartDateMinus1month.month() + 1);
  const paymentStartDateMinus1monthAsdPresentServiceYear = paymentStartDateMinus1monthCareerMonth.asdPresentServiceYear;
  const paymentStartDateMinus1monthAsdPresentServiceMonth = paymentStartDateMinus1monthCareerMonth.asdPresentServiceMonth;
  const paymentStartDateMinus1monthServiceTime = ((paymentStartDateMinus1monthAsdPresentServiceYear * 12) + paymentStartDateMinus1monthAsdPresentServiceMonth) / 12;

  //  Determine the total active duty service time based on starting/ending components. RC member's can attain a regular retirement.
  const activeDutyServiceTime = serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.AC_2_AC
    ? paymentStartDateMinus1monthServiceTime
    : getRcActiveDutyServiceTime(serviceMember);

  //  Get sum of pay amount from 1 month prior to the 1st Hi3 pension payment to 36 months in the past.
  const retSepDateMinus36Months = moment(serviceMember.firstMonthOutOfServiceDate()).subtract(36, 'months');
  const hi3ServiceMonthStart = careerMonths.find((o) => o.calendarYear === retSepDateMinus36Months.year() && o.calendarMonth === retSepDateMinus36Months.month() + 1);
  const hi3ServiceMonthEnd = careerMonths.find((o) => o.calendarYear === retSepDate.year() && o.calendarMonth === retSepDate.month() + 1);
  const hi3ServiceMonths = careerMonths.filter((o) => o.recordId >= hi3ServiceMonthStart.recordId && o.recordId <= hi3ServiceMonthEnd.recordId);

  //  Get Hi3 total and average.
  const hi3PensionPaySum = sumBy(hi3ServiceMonths, 'payAmountTodaysDollars');
  const hi3PensionPayAverage = hi3PensionPaySum / hi3ServiceMonths.length;

  careerMonths.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.hi3PensionAmountTodaysDollars = 0;
    if (calendarDate.isSame(paymentStartDate)) {
      careerMonth.hi3PensionAmountTodaysDollars = serviceMember.hi3PensionPercentage() * activeDutyServiceTime * hi3PensionPayAverage;
    } else if (calendarDate.isAfter(paymentStartDate)) {
      careerMonth.hi3PensionAmountTodaysDollars = careerMonths[index - 1].hi3PensionAmountTodaysDollars;
    }

    if (serviceMember.moduleSettings.details.calculatorName === 'Hi3') careerMonth.pensionAmountTodaysDollars = careerMonth.hi3PensionAmountTodaysDollars;
  });

  serviceMember.hi3.todaysDollarsAverage = hi3PensionPayAverage;
  return careerMonths;
}

//  ****************************************************************************************************
//    Calculate the Hi3 Pension for a non-regular retirement.
//    For Today's Dollars, the payout amount is the same for all months starting at the provided
//    non-regular retirement withdrawal date thru death month.
//  ****************************************************************************************************
export function addHi3PensionPaymentsNonRegularRetirementTodaysDollars(careerMonths, serviceMember, paymentStartDate) {
  //  Get sum of pay amount from 1 month prior to the 1st Hi3 pension payment to 36 months in the past.
  const nonRegularRetirementWithdrawalDateMinus1month = moment(serviceMember.nonRegularRetirementWithdrawalDate()).subtract(1, 'months');
  const nonRegularRetirementWithdrawalDateMinus36Months = moment(serviceMember.nonRegularRetirementWithdrawalDate()).subtract(36, 'months');
  const hi3ServiceMonthStart = careerMonths.find((o) => o.calendarYear === nonRegularRetirementWithdrawalDateMinus36Months.year() && o.calendarMonth === nonRegularRetirementWithdrawalDateMinus36Months.month() + 1);
  const hi3ServiceMonthEnd = careerMonths.find((o) => o.calendarYear === nonRegularRetirementWithdrawalDateMinus1month.year() && o.calendarMonth === nonRegularRetirementWithdrawalDateMinus1month.month() + 1);
  const hi3ServiceMonths = careerMonths.filter((o) => o.recordId >= hi3ServiceMonthStart.recordId && o.recordId <= hi3ServiceMonthEnd.recordId);

  //  Get Hi3 total and average.
  const hi3PensionPaySum = sumBy(hi3ServiceMonths, 'payAmountTodaysDollars');
  const hi3PensionPayAverage = hi3PensionPaySum / hi3ServiceMonths.length;

  careerMonths.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    careerMonth.hi3PensionAmountTodaysDollars = 0;
    careerMonth.pensionAmountTodaysDollars = 0;

    if (calendarDate.isSame(paymentStartDate)) {
      careerMonth.hi3PensionAmountTodaysDollars = serviceMember.hi3PensionPercentage() * serviceMember.results.reserveComponent.multiplier * hi3PensionPayAverage;
    } else if (calendarDate.isAfter(paymentStartDate)) {
      careerMonth.hi3PensionAmountTodaysDollars = careerMonths[index - 1].hi3PensionAmountTodaysDollars;
    }

    if (serviceMember.moduleSettings.details.calculatorName === 'Hi3') careerMonth.pensionAmountTodaysDollars = careerMonth.hi3PensionAmountTodaysDollars;
  });

  serviceMember.hi3.todaysDollarsAverage = hi3PensionPayAverage;
  return careerMonths;
}

export function addHi3PensionPaymentsRegularRetirementFutureDollars(careerMonths, serviceMember, paymentStartDate, retSepDate) {
  //  Compute active duty service time from 1 month prior to the ret/sep date.
  const paymentStartDateMinus1month = moment(paymentStartDate).subtract(1, 'months');
  const paymentStartDateMinus1monthCareerMonth = careerMonths.find((o) => o.calendarYear === paymentStartDateMinus1month.year() && o.calendarMonth === paymentStartDateMinus1month.month() + 1);
  const paymentStartDateMinus1monthAsdPresentServiceYear = paymentStartDateMinus1monthCareerMonth.asdPresentServiceYear;
  const paymentStartDateMinus1monthAsdPresentServiceMonth = paymentStartDateMinus1monthCareerMonth.asdPresentServiceMonth;
  const paymentStartDateMinus1monthServiceTime = ((paymentStartDateMinus1monthAsdPresentServiceYear * 12) + paymentStartDateMinus1monthAsdPresentServiceMonth) / 12;

  //  Determine the total active duty service time based on starting/ending components. RC member's can attain a regular retirement.
  const activeDutyServiceTime = serviceMember.componentMatrix() === serviceMember.COMPONENT_MATRICES.AC_2_AC
    ? paymentStartDateMinus1monthServiceTime
    : getRcActiveDutyServiceTime(serviceMember);

  //  Get sum of pay amount from 1 month prior to receiving the 1st Hi3 pension payment to 36 months in the past.
  const retSepDateMinus36Months = moment(serviceMember.firstMonthOutOfServiceDate()).subtract(36, 'months');
  const hi3ServiceMonthStart = careerMonths.find((o) => o.calendarYear === retSepDateMinus36Months.year() && o.calendarMonth === retSepDateMinus36Months.month() + 1);
  const hi3ServiceMonthEnd = careerMonths.find((o) => o.calendarYear === retSepDate.year() && o.calendarMonth === retSepDate.month() + 1);
  const hi3ServiceMonths = careerMonths.filter((o) => o.recordId >= hi3ServiceMonthStart.recordId && o.recordId <= hi3ServiceMonthEnd.recordId);

  //  Get Hi3 total and average.
  const hi3PensionPaySum = sumBy(hi3ServiceMonths, 'payAmountFutureDollars');
  const hi3PensionPayAverage = hi3PensionPaySum / hi3ServiceMonths.length;

  careerMonths.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    let hi3PensionColaGrowthRate = 0;

    careerMonth.hi3PensionColaGrowthRate = 0;
    careerMonth.hi3PensionAmountFutureDollars = 0;
    careerMonth.pensionColaGrowthRate = 0;
    careerMonth.pensionAmountFutureDollars = 0;

    if (calendarDate.year() === paymentStartDate.year()) {
      if (calendarDate.isSame(paymentStartDate)) {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = serviceMember.hi3PensionPercentage() * activeDutyServiceTime * hi3PensionPayAverage;
      } else {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = careerMonths[index - 1].hi3PensionAmountFutureDollars;
      }
    } else if (calendarYear > paymentStartDate.year()) {
      let colaGrowthRateAdjustment = 1;

      if (calendarMonth === 1) {
        switch (calendarYear - serviceMember.firstMonthOutOfServiceDate().year()) {
          case 1:
            colaGrowthRateAdjustment = serviceMember.getColaAdjustmentRate(paymentStartDate.month());
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year1);
            break;
          case 2:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year2);
            break;
          case 3:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year3);
            break;
          case 4:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year4);
            break;
          case 5:
          default:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year5);
            break;
        }

        //  Calculate COLA growth rate.
        hi3PensionColaGrowthRate = hi3PensionColaGrowthRate * colaGrowthRateAdjustment;

        //  Safe guard against 0%.
        hi3PensionColaGrowthRate = hi3PensionColaGrowthRate == 0 ? 1 : hi3PensionColaGrowthRate / 100 + 1;

        //  Calculate pension amount.
        careerMonth.hi3PensionAmountFutureDollars = toNumber(careerMonths[index - 1].hi3PensionAmountFutureDollars) * hi3PensionColaGrowthRate;

        //  Persist data to all-numbers table. When value = 0, does NOT display a value in all-numbers table.
        careerMonth.colaGrowthRateAdjustment = colaGrowthRateAdjustment === 1 ? 0 : colaGrowthRateAdjustment;
        careerMonth.hi3PensionColaGrowthRate = hi3PensionColaGrowthRate === 1 ? 0 : hi3PensionColaGrowthRate;
      }
      else {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = toNumber(careerMonths[index - 1].hi3PensionAmountFutureDollars);
      }
    }

    if (serviceMember.moduleSettings.details.calculatorName === 'Hi3') {
      careerMonth.pensionColaGrowthRate = careerMonth.hi3PensionColaGrowthRate;
      careerMonth.pensionAmountFutureDollars = careerMonth.hi3PensionAmountFutureDollars;
    }
  });

  serviceMember.hi3.futureDollarsAverage = hi3PensionPayAverage;
  return careerMonths;
}

export function addHi3PensionPaymentsNonRegularRetirementFutureDollars(careerMonths, serviceMember, paymentStartDate) {
  //  Retrieve 36 career months starting at 1 month prior to the non-regular retirement withdrawal date.
  const nonRegularRetirementWithdrawalDateMinus1month = moment(serviceMember.nonRegularRetirementWithdrawalDate()).subtract(1, 'months');
  const nonRegularRetirementWithdrawalDateMinus36Months = moment(serviceMember.nonRegularRetirementWithdrawalDate()).subtract(36, 'months');
  const hi3ServiceMonthStart = careerMonths.find((o) => o.calendarYear === nonRegularRetirementWithdrawalDateMinus36Months.year() && o.calendarMonth === nonRegularRetirementWithdrawalDateMinus36Months.month() + 1);
  const hi3ServiceMonthEnd = careerMonths.find((o) => o.calendarYear === nonRegularRetirementWithdrawalDateMinus1month.year() && o.calendarMonth === nonRegularRetirementWithdrawalDateMinus1month.month() + 1);
  const hi3ServiceMonths = careerMonths.filter((o) => o.recordId >= hi3ServiceMonthStart.recordId && o.recordId <= hi3ServiceMonthEnd.recordId);

  //  Get Hi3 total and average.
  const hi3PensionPaySum = sumBy(hi3ServiceMonths, 'payAmountFutureDollars');
  const hi3PensionPayAverage = hi3PensionPaySum / hi3ServiceMonths.length;

  careerMonths.forEach((careerMonth, index) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    let hi3PensionColaGrowthRate = 0;

    careerMonth.hi3PensionColaGrowthRate = 0;
    careerMonth.hi3PensionAmountFutureDollars = 0;
    careerMonth.pensionAmountFutureDollars = 0;

    if (calendarYear === paymentStartDate.year()) {
      if (calendarDate.isSame(paymentStartDate)) {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = serviceMember.hi3PensionPercentage() * serviceMember.results.reserveComponent.multiplier * hi3PensionPayAverage;
      } else {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = careerMonths[index - 1].hi3PensionAmountFutureDollars;
      }
    } else if (calendarYear > paymentStartDate.year()) {
      let colaGrowthRateAdjustment = 1;

      if (calendarMonth === 1) {
        switch (calendarYear - serviceMember.firstMonthOutOfServiceDate().year()) {
          case 1:
            colaGrowthRateAdjustment = serviceMember.getColaAdjustmentRate(paymentStartDate.month());
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year1);
            break;
          case 2:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year2);
            break;
          case 3:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year3);
            break;
          case 4:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year4);
            break;
          case 5:
          default:
            hi3PensionColaGrowthRate = toNumber(serviceMember.globalSettings.colaGrowthRates.year5);
            break;
        }

        //  Calculate COLA growth rate.
        hi3PensionColaGrowthRate = hi3PensionColaGrowthRate * colaGrowthRateAdjustment;

        //  Safe guard against 0%.
        hi3PensionColaGrowthRate = hi3PensionColaGrowthRate === 0 || hi3PensionColaGrowthRate / 100 + 1 < 1 ? 1 : hi3PensionColaGrowthRate / 100 + 1;

        //  Calculate pension amount.
        careerMonth.hi3PensionAmountFutureDollars = toNumber(careerMonths[index - 1].hi3PensionAmountFutureDollars) * hi3PensionColaGrowthRate;

        //  Persist data to all-numbers table. When value = 0, does NOT display a value in all-numbers table.
        careerMonth.colaGrowthRateAdjustment = colaGrowthRateAdjustment === 1 ? 0 : colaGrowthRateAdjustment;
        careerMonth.hi3PensionColaGrowthRate = hi3PensionColaGrowthRate === 1 ? 0 : hi3PensionColaGrowthRate;
      } else {
        careerMonth.hi3PensionColaGrowthRate = 0;
        careerMonth.hi3PensionAmountFutureDollars = toNumber(careerMonths[index - 1].hi3PensionAmountFutureDollars);
      }
    }

    if (serviceMember.moduleSettings.details.calculatorName === 'Hi3') {
      careerMonth.pensionColaGrowthRate = careerMonth.hi3PensionColaGrowthRate;
      careerMonth.pensionAmountFutureDollars = careerMonth.hi3PensionAmountFutureDollars;
    }
  });

  serviceMember.hi3.futureDollarsAverage = hi3PensionPayAverage;
  return careerMonths;
}
